import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Table, Form, Button, Card, Row, Col, Badge, Spinner as BSSpinner, Alert } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { Alertassw } from '../Alertas/Alertassw'
import { Modales } from '../Modales'
import { Accesorios } from './Accesorios'
import { Editareq } from '../Equipo/Editareq'
import { getDataobtener } from '../../api/common'
import { computarGrupoInfoMap } from '../../utils/gruposEquipos'
import ModalIncluirDependienteIngreso from './ModalIncluirDependienteIngreso'

/** Verifica si un EquipmentInOt está en la lista `Eqselect` (por _id de EquipmentInOt). */
export function Inarray(array, objeto) {
  return array.some((item) => item._id === objeto._id)
}

/** ¿El equipo está disponible para ingreso? (No Finalizado y no ya ingresado). */
const esIngresable = (eq) =>
  eq?.estado === 'No Finalizado' && eq?.equipo?.movimiento !== 'Ingresado'

export const Equiposot = ({
  ordendata,
  setOrdendata,
  setEqselect,
  Eqselect,
  setPestaña,
  bodyingreso,
  setBodyingreso,
}) => {
  const [modalAccesorios, setModalAccesorios] = useState(false)
  const [modalEditar, setModalEditar]           = useState(false)
  const [equipoinfo, setEquipoinfo]             = useState(null)
  const [equnico, setEqunico]                   = useState({})
  const [refresh, setRefresh]                   = useState(false)
  const [loading, setLoading]                   = useState(false)

  // Filtros
  const [filterItem,  setFilterItem]  = useState('')
  const [filterSerie, setFilterSerie] = useState('')
  const [filterSede,  setFilterSede]  = useState('')

  // Modal de sincronización padre↔hijo
  const [modalHijos, setModalHijos] = useState(null) // null | { padre, hijos }
  // Cola de hijos pendientes de capturar accesorios (opción "por separado")
  const [colaHijosSeparados, setColaHijosSeparados] = useState([])

  // ── Consulta OT ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ordendata?._id) return
    let cancelado = false
    const consultarOt = async () => {
      setLoading(true)
      try {
        const result = await getDataobtener(ordendata._id)
        if (cancelado) return
        const otFresca = result.data.data
        setOrdendata(otFresca)
        // Sincronizar snapshot de los ya seleccionados con datos frescos del equipo físico.
        if (Eqselect.length > 0) {
          const clones = JSON.parse(JSON.stringify(Eqselect))
          const equiposFrescos = otFresca.equipos || []
          clones.forEach((sel) => {
            const fresco = equiposFrescos.find((e) => e._id === sel._id)
            if (fresco) sel.equipo = fresco.equipo
          })
          setEqselect(clones)
        }
      } catch (err) {
        console.error('[Equiposot] consultarOt:', err)
        Alertassw('error', 'No se pudo cargar la OT')
      } finally {
        if (!cancelado) setLoading(false)
      }
    }
    consultarOt()
    return () => { cancelado = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ordendata?._id])

  // ── Grupos padre↔hijos ──────────────────────────────────────────────────
  const equiposOt = ordendata?.equipos || []

  const grupoInfoByIdx = useMemo(() => {
    const info = computarGrupoInfoMap(equiposOt)
    const byIdx = new Map()
    equiposOt.forEach((eq, idx) => {
      const g = info.get(String(eq._id))
      if (g) byIdx.set(idx, g)
    })
    return byIdx
  }, [equiposOt])

  // Dado idx de padre, devuelve los índices de sus hijos disponibles.
  const hijosDePadre = useCallback((padreIdx) => {
    const padre = equiposOt[padreIdx]
    const padreFisicoId = String(padre?.equipo?._id || padre?.equipo || '')
    if (!padreFisicoId) return []
    return equiposOt.filter((h) => {
      const dep = h?.equipo?.equipoDependiente
      return dep && String(dep._id || dep) === padreFisicoId
    })
  }, [equiposOt])

  // Dado idx de hijo, devuelve idx del padre visible (-1 si no).
  const padreIdxDeHijo = useCallback((hijoIdx) => {
    const h = equiposOt[hijoIdx]
    const dep = h?.equipo?.equipoDependiente
    if (!dep) return -1
    const padreFisicoId = String(dep._id || dep)
    return equiposOt.findIndex(
      (x) => String(x?.equipo?._id || x?.equipo || '') === padreFisicoId
    )
  }, [equiposOt])

  // ── Filtros ─────────────────────────────────────────────────────────────
  const sedeOptions = useMemo(() => {
    const set = new Set()
    equiposOt.forEach((eq) => {
      const s = eq.snapshot?.sede ?? eq?.equipo?.sede
      if (s) set.add(s)
    })
    return Array.from(set).sort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equiposOt])

  // Extractores robustos: buscan primero en snapshot (moderno) y caen a
  // equipo.item.nombre / equipo.serie (legacy) para no ocultar filas cuando el
  // snapshot está vacío en equipos antiguos.
  const getItemNombre = (eq) =>
    (eq?.snapshot?.item ??
      eq?.equipo?.item?.nombre ??
      eq?.equipo?.item ??
      '').toString()
  const getSerial = (eq) =>
    (eq?.snapshot?.serial ??
      eq?.equipo?.serie ??
      '').toString()
  const getSede = (eq) =>
    (eq?.snapshot?.sede ??
      eq?.equipo?.sede ??
      '').toString()

  const equiposFiltrados = useMemo(() => {
    let lista = equiposOt.map((item, idx) => ({ item, idx }))
    if (filterItem.trim()) {
      const q = filterItem.toLowerCase()
      lista = lista.filter(({ item }) => getItemNombre(item).toLowerCase().includes(q))
    }
    if (filterSerie.trim()) {
      const q = filterSerie.toLowerCase()
      lista = lista.filter(({ item }) => getSerial(item).toLowerCase().includes(q))
    }
    if (filterSede) {
      lista = lista.filter(({ item }) => getSede(item) === filterSede)
    }
    return lista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equiposOt, filterItem, filterSerie, filterSede])

  // ── Selección + sincronización padre↔hijos ──────────────────────────────
  const removerDeEqselect = (eqInOt) => {
    setEqselect((prev) => prev.filter((s) => s._id !== eqInOt._id))
  }

  const handleCheckbox = (eq, checked) => {
    if (!checked) {
      // ¿Es padre con hijos auto-agregados?
      const info = grupoInfoByIdx.get(equiposOt.findIndex((e) => e._id === eq._id))
      const esPadre = info?.rol === 'padre'
      const hijos = esPadre ? hijosDePadre(equiposOt.findIndex((e) => e._id === eq._id)) : []
      const hijosAutoEnSel = hijos.filter((h) => {
        const sel = Eqselect.find((s) => s._id === h._id)
        return sel && sel.origenSeleccion === 'auto'
      })
      if (esPadre && hijosAutoEnSel.length > 0) {
        Swal.fire({
          icon: 'question',
          title: '¿Retirar también los dependientes?',
          text: `El equipo ${eq.snapshot?.item} tiene ${hijosAutoEnSel.length} dependiente(s) incluido(s) automáticamente.`,
          showCancelButton: true,
          confirmButtonText: 'Sí, retirar ambos',
          cancelButtonText: 'No, solo el padre',
        }).then((res) => {
          removerDeEqselect(eq)
          if (res.isConfirmed) {
            setEqselect((prev) =>
              prev.filter((s) => !hijosAutoEnSel.some((h) => h._id === s._id))
            )
          }
        })
        return
      }
      removerDeEqselect(eq)
      return
    }

    // Al marcar: abre modal Accesorios para capturar los datos del equipo.
    setEqunico(eq)
    setModalAccesorios(true)
  }

  // Callback invocado por `Accesorios` cuando el usuario clickea "Seleccionar".
  // Recibe el equipo YA ENRIQUECIDO (con accesorios/observacion/fotos capturados)
  // como parámetro — evita el race condition de leer `Eqselect` con setState pendiente.
  const onEquipoIngresado = (equipoEnriquecido) => {
    setModalAccesorios(false)
    if (!equipoEnriquecido) return
    // Detectar si es padre con hijos disponibles → abrir modal de sincronización.
    const idx = equiposOt.findIndex((e) => e._id === equipoEnriquecido._id)
    const info = grupoInfoByIdx.get(idx)
    const esPadre = info?.rol === 'padre'
    if (!esPadre) return
    const hijosDisp = hijosDePadre(idx).filter(esIngresable)
    if (hijosDisp.length > 0) {
      setModalHijos({ padre: equipoEnriquecido, hijos: hijosDisp })
    }
  }

  // Al cerrar modal SIN agregar (usuario canceló): confirmar antes de eliminar
  // fotos ya subidas para no perder trabajo.
  const cancelarModalAccesorios = async () => {
    const fotosPendientes = Array.isArray(equnico?.fotos) ? equnico.fotos : []
    if (fotosPendientes.length === 0) {
      setModalAccesorios(false)
      return
    }
    const res = await Swal.fire({
      icon: 'warning',
      title: '¿Descartar fotos capturadas?',
      text: `Se eliminarán ${fotosPendientes.length} foto(s) del almacenamiento.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, descartar',
      cancelButtonText: 'Volver al modal',
    })
    if (res.isConfirmed) {
      // Nota: `Accesorios` ahora usa state local, no muta `equnico.fotos`. El fotos
      // pendientes viven en el state interno; para descartarlas necesitaríamos un
      // ref o callback. Por ahora, cerramos el modal — el usuario puede volver a
      // capturar. En una iteración posterior, agregar callback `onCancelar` al modal.
      setModalAccesorios(false)
    }
  }

  // Modal de hijos: opciones
  const onIncluirCompartiendo = () => {
    if (!modalHijos) return
    const { padre, hijos } = modalHijos
    const nuevos = hijos.map((h) => ({
      ...h,
      accesorios: padre.accesorios,
      observacion: padre.observacion,
      fotos: [...(padre.fotos || [])], // clonar por VALOR (no compartir referencia)
      origenSeleccion: 'auto',
    }))
    setEqselect((prev) => [...prev, ...nuevos])
    setModalHijos(null)
  }
  const onIncluirPorSeparado = () => {
    if (!modalHijos) return
    setColaHijosSeparados([...modalHijos.hijos])
    setModalHijos(null)
    // Abrir modal Accesorios para el primer hijo.
    const [primero, ...resto] = modalHijos.hijos
    if (primero) {
      setEqunico({ ...primero, origenSeleccion: 'auto' })
      setColaHijosSeparados(resto)
      setModalAccesorios(true)
    }
  }
  const onSoloPadre = () => setModalHijos(null)
  const onCancelarModalHijos = () => {
    // Deshacer el marcado del padre.
    if (modalHijos) removerDeEqselect(modalHijos.padre)
    setModalHijos(null)
  }

  // Cuando el Accesorios cierra y aún hay hijos en cola → seguir.
  useEffect(() => {
    if (modalAccesorios) return
    if (colaHijosSeparados.length === 0) return
    const [siguiente, ...resto] = colaHijosSeparados
    setEqunico({ ...siguiente, origenSeleccion: 'auto' })
    setColaHijosSeparados(resto)
    setModalAccesorios(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalAccesorios])

  // ── Editar equipo ───────────────────────────────────────────────────────
  const Editequipo = (eq) => {
    setEquipoinfo(eq)
    setModalEditar(true)
  }

  // ── Continuar: construir bodyingreso.equipos ────────────────────────────
  const continuar = () => {
    if (Eqselect.length === 0) {
      Alertassw('error', 'Equipos no seleccionados')
      return
    }
    const eqbody = Eqselect.map((eq) => ({
      equipo: eq._id,
      accesorios: eq.accesorios,
      observacion: eq.observacion,
      fotos: eq.fotos,
    }))
    setBodyingreso({ ...bodyingreso, equipos: eqbody })
    setPestaña(2)
  }

  const seleccionadosCount = Eqselect.length
  const totalDisponibles = equiposFiltrados.filter(({ item }) => esIngresable(item)).length

  return (
    <div>
      <Modales
        estado={modalEditar}
        cambiarestado={setModalEditar}
        titulo="Editar equipo"
      >
        <Editareq
          infoOt={ordendata}
          refrescar={setRefresh}
          refresh={refresh}
          infoequipo={equipoinfo}
          cambiarestado={setModalEditar}
        />
      </Modales>

      <Modales
        estado={modalAccesorios}
        cambiarestado={cancelarModalAccesorios}
        titulo={`Ingresar: ${equnico?.snapshot?.item || ''}`}
      >
        <Accesorios
          equnico={equnico}
          Eqselect={Eqselect}
          setEqselect={setEqselect}
          setEstadomodal={setModalAccesorios}
          onIngresado={onEquipoIngresado}
        />
      </Modales>

      <ModalIncluirDependienteIngreso
        show={Boolean(modalHijos)}
        padre={modalHijos?.padre}
        hijos={modalHijos?.hijos || []}
        onIncluirCompartiendo={onIncluirCompartiendo}
        onIncluirPorSeparado={onIncluirPorSeparado}
        onSoloPadre={onSoloPadre}
        onCancelar={onCancelarModalHijos}
      />

      <Card className="mb-3 border-0 shadow-sm w-100">
        <Card.Header className="py-2 bg-white fw-semibold text-secondary w-100">
          <i className="bi bi-sliders me-2" />
          Filtros
        </Card.Header>
        <Card.Body className="py-3 w-100">
          <Row className="g-2 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label className="small fw-semibold mb-1">Ítem</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Buscar por ítem…"
                value={filterItem}
                onChange={(e) => setFilterItem(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small fw-semibold mb-1">Serie</Form.Label>
              <Form.Control
                size="sm"
                placeholder="Buscar por serie…"
                value={filterSerie}
                onChange={(e) => setFilterSerie(e.target.value)}
              />
            </Col>
            <Col xs={12} md={4}>
              <Form.Label className="small fw-semibold mb-1">Sede</Form.Label>
              <Form.Select
                size="sm"
                value={filterSede}
                onChange={(e) => setFilterSede(e.target.value)}
              >
                <option value="">Todas</option>
                {sedeOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <div className="d-flex align-items-center gap-2 mb-2">
        <Badge bg={seleccionadosCount > 0 ? 'primary' : 'secondary'}>
          {seleccionadosCount} seleccionado(s) · {totalDisponibles} disponible(s)
        </Badge>
        <Button
          variant="primary"
          size="sm"
          onClick={continuar}
          disabled={seleccionadosCount === 0}
          className="ms-auto"
          title={seleccionadosCount === 0 ? 'Selecciona al menos un equipo' : ''}
        >
          Continuar ({seleccionadosCount}) <i className="bi bi-arrow-right ms-1" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center my-5">
          <BSSpinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando equipos de la OT…</p>
        </div>
      ) : equiposFiltrados.length === 0 ? (
        <Alert variant="info" className="text-center">
          <i className="bi bi-inbox fs-3 d-block mb-2" />
          No hay equipos que coincidan con los filtros.
        </Alert>
      ) : (
        <Table hover striped responsive size="sm">
          <thead className="table-dark">
            <tr>
              <th style={{ width: 42 }} className="text-center" title="Relación padre/dependiente">
                <i className="bi bi-diagram-3" />
              </th>
              <th>Ítem</th>
              <th>Marca</th>
              <th>Modelo</th>
              <th>Serie</th>
              <th>Sede</th>
              <th>Inventario</th>
              <th>Consec.</th>
              <th>Estado</th>
              <th className="text-center">Editar</th>
              <th className="text-center">Ingresar</th>
            </tr>
          </thead>
          <tbody>
            {equiposFiltrados.map(({ item: eq, idx }) => {
              const g = grupoInfoByIdx.get(idx)
              const isChecked = Inarray(Eqselect, eq)
              const yaIngresado = eq?.equipo?.movimiento === 'Ingresado'
              const disponible = esIngresable(eq)
              // Deshabilitar checkbox del hijo si el padre está visible y no seleccionado en modo auto
              const esHijoConPadreVisible = g?.rol === 'hijo' && padreIdxDeHijo(idx) !== -1
              const disabledCheckbox = !disponible || (esHijoConPadreVisible && isChecked === false)

              return (
                <tr key={eq._id} className={isChecked ? 'table-primary' : ''}>
                  {/* Pareja */}
                  <td className="text-center align-middle" style={{ padding: '2px 1px' }}>
                    {g ? (
                      <span
                        className={g.rol === 'padre' ? 'bg-primary text-white' : 'bg-info text-dark'}
                        style={{
                          display: 'inline-block',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          padding: '2px 4px',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          letterSpacing: '-0.03em',
                          borderRadius: '3px',
                        }}
                        title={
                          g.rol === 'padre'
                            ? `Padre del grupo ${g.grupoNum}`
                            : `Dependiente ${g.grupoNum}${g.letra || ''} del grupo ${g.grupoNum}`
                        }
                      >
                        {g.rol === 'padre' ? `P${g.grupoNum}` : `D${g.grupoNum}${g.letra || ''}`}
                      </span>
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.7rem' }}>—</span>
                    )}
                  </td>

                  <td>{eq.snapshot?.item}</td>
                  <td>{eq.snapshot?.marca}</td>
                  <td>{eq.snapshot?.modelo}</td>
                  <td>{eq.snapshot?.serial}</td>
                  <td>{eq.snapshot?.sede}</td>
                  <td>{eq.snapshot?.inventario}</td>
                  <td className="text-center">
                    <Badge bg="secondary">{eq.consecutivo || 'N/A'}</Badge>
                  </td>
                  <td>
                    {yaIngresado ? (
                      <Badge bg="secondary" title="Ya está ingresado en el laboratorio">
                        Ya ingresado
                      </Badge>
                    ) : (
                      <Badge bg={disponible ? 'warning' : 'light'} text={disponible ? 'dark' : 'muted'}>
                        {eq.estado}
                      </Badge>
                    )}
                  </td>
                  <td className="text-center">
                    <i
                      className="bi bi-pencil-square text-secondary"
                      onClick={() => Editequipo(eq)}
                      style={{ cursor: 'pointer' }}
                      title="Editar equipo"
                    />
                  </td>
                  <td className="text-center" onClick={(e) => e.stopPropagation()}>
                    {disponible ? (
                      <Form.Check
                        type="checkbox"
                        checked={isChecked}
                        disabled={disabledCheckbox}
                        onChange={(e) => handleCheckbox(eq, e.target.checked)}
                        title={
                          esHijoConPadreVisible && !isChecked
                            ? 'Se sincroniza al seleccionar el equipo padre'
                            : ''
                        }
                      />
                    ) : (
                      <span className="text-muted small">N/A</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </div>
  )
}
